
import React from "react";
import { Mail, Send } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";

type FormData = {
  name: string;
  email: string;
  message: string;
};

const Contact = () => {
  const { toast } = useToast();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>();

  const onSubmit = (data: FormData) => {
    console.log("Form submitted:", data);
    
    // In a real app, you would send this data to your backend
    toast({
      title: "Message sent",
      description: "We'll get back to you as soon as possible!",
    });
    
    // Reset form
    reset();
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Header />
      
      <main className="flex-grow pt-24">
        <section className="py-20 relative">
          <div className="shape-blob w-[300px] h-[300px] top-1/4 left-[5%] bg-primary/10 animate-blob" 
            style={{ animationDelay: '4s' }}></div>
          
          <div className="container px-4 md:px-6 relative z-10">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="px-4 py-1.5 bg-secondary/10 text-secondary mb-4 rounded-full text-sm font-medium">
                  <Mail className="w-4 h-4 mr-1" /> GET IN TOUCH
                </Badge>
                <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
                  Contact Us
                </h2>
                <p className="mt-4 text-xl text-muted-foreground max-w-2xl mx-auto">
                  Have questions about Retail Ranker? We're here to help you boost your local business presence.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-[2rem] shadow-playful p-8 border-2 border-secondary/10">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="block text-sm font-medium">
                      Your Name
                    </label>
                    <Input
                      id="name"
                      placeholder="John Smith"
                      {...register("name", { required: "Name is required" })}
                      className={errors.name ? "border-destructive" : ""}
                    />
                    {errors.name && (
                      <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-medium">
                      Email Address
                    </label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      {...register("email", { 
                        required: "Email is required",
                        pattern: {
                          value: /\S+@\S+\.\S+/,
                          message: "Please enter a valid email address"
                        }
                      })}
                      className={errors.email ? "border-destructive" : ""}
                    />
                    {errors.email && (
                      <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="block text-sm font-medium">
                      Message
                    </label>
                    <Textarea
                      id="message"
                      placeholder="How can we help you?"
                      rows={5}
                      {...register("message", { required: "Message is required" })}
                      className={errors.message ? "border-destructive" : ""}
                    />
                    {errors.message && (
                      <p className="text-sm text-destructive mt-1">{errors.message.message}</p>
                    )}
                  </div>

                  <Button type="submit" className="w-full rounded-xl h-12" size="lg">
                    Send Message <Send className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      
    </div>
  );
};

export default Contact;
